import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ZoomVideo, {
  VideoQuality,
  WhiteboardSharePermissionCode,
  WhiteboardInitiatePermissionCode,
  WhiteboardStatus,
} from "@zoom/videosdk";
import { FaMicrophone, FaDesktop, FaChalkboard } from "react-icons/fa";
import {
  BsCameraVideo,
  BsCameraVideoFill,
  BsRecordCircle,
  BsRecordCircleFill,
  BsPauseFill,
  BsPlayFill,
} from "react-icons/bs";
import { MdCallEnd } from "react-icons/md";
import {
  ZOOM_CONFIG,
  ROLE_HOST,
  ROLE_PARTICIPANT,
  SESSION_PARAM,
  generateSessionName,
  buildMeetingLink,
  buildZoomSignature,
} from "./zoomShared";
import RemoteCompass from "./RemoteCompass";

// Lets a page refresh rejoin whatever meeting was active instead of dumping
// the user back on the SDK sandbox screen — sessionStorage (not localStorage)
// so it survives a reload but doesn't linger once the tab is actually closed.
const ACTIVE_MEETING_STORAGE_KEY = "zoomActiveMeeting";

const readActiveMeeting = () => {
  try {
    const raw = sessionStorage.getItem(ACTIVE_MEETING_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    console.debug("[ZoomTest] readActiveMeeting", parsed);
    return parsed;
  } catch (e) {
    console.warn("[ZoomTest] readActiveMeeting failed", e);
    return null;
  }
};

const writeActiveMeeting = (meeting) => {
  try {
    sessionStorage.setItem(ACTIVE_MEETING_STORAGE_KEY, JSON.stringify(meeting));
    console.debug("[ZoomTest] writeActiveMeeting", meeting);
  } catch (e) {
    console.warn("[ZoomTest] writeActiveMeeting failed", e);
  }
};

const clearActiveMeeting = () => {
  try {
    sessionStorage.removeItem(ACTIVE_MEETING_STORAGE_KEY);
    console.debug("[ZoomTest] clearActiveMeeting");
  } catch (e) {
    console.warn("[ZoomTest] clearActiveMeeting failed", e);
  }
};

// Zoom Video SDK's web self-view has a long-reported bug specific to iOS
// Safari (see Zoom devforum threads on attachVideo/self-view rendering
// black on Safari) where the very first attachVideo() right after starting
// the camera renders a black tile that only actually shows a picture once
// the camera is stopped and started again. Detected here so that recovery
// cycle can be triggered automatically instead of requiring the user to
// notice a black tile and re-click the camera button themselves.
const isIOSSafariBrowser = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOSDevice =
    /iP(hone|od|iPad)/.test(ua) ||
    // iPadOS 13+ reports its platform as "MacIntel" like a real Mac —
    // touch support is the only reliable way to tell them apart.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|crios|fxios|edgios|android).)*safari/i.test(ua);
  return isIOSDevice && isSafari;
};

export default function ZoomAppointment() {
  const location = useLocation();
  const navigate = useNavigate();
  // Present when we were routed here from a booking flow (e.g. the master's
  // "Meet Now" button) instead of opened directly as the manual test sandbox.
  const bookingState = location.state || null;
  const bookingParticipants = Array.isArray(bookingState?.bookingParticipants)
    ? bookingState.bookingParticipants.filter((p) => p?.custID)
    : [];

  // What to auto-rejoin on mount, if anything, in priority order:
  // 1. A fresh booking-flow navigation (e.g. "Meet Now") — most specific.
  // 2. Whatever meeting sessionStorage says was active in this tab before a
  //    refresh.
  // 3. A logged-in master's own room. A master's Zoom session is always
  //    deterministic — the same room every time, keyed off their own
  //    name/code (see MasterComponents/UpcomingConsultation.jsx's
  //    handleMeetNow) — so unlike a guest, they don't actually need
  //    sessionStorage (or anything tab-scoped) to find their way back to it;
  //    this is resolved async below via their own profile lookup and only
  //    used when nothing more specific already answered the question.
  const [storedMeeting] = useState(() =>
    bookingState?.sessionName ? null : readActiveMeeting(),
  );
  const [masterIdentity, setMasterIdentity] = useState(null);
  const [masterIdentityChecked, setMasterIdentityChecked] = useState(false);

  const resumeTarget = bookingState?.sessionName
    ? {
        sessionName: bookingState.sessionName,
        isHost: !!bookingState.isHost,
        userName: bookingState.userName,
      }
    : storedMeeting || masterIdentity;
  // True only while we're still waiting to find out if the logged-in user is
  // a master with a personal room to fall back to — lets the render below
  // show a neutral "checking" state instead of flashing the sandbox and then
  // swapping to "Reconnecting…" a moment later.
  const resolvingIdentity = !resumeTarget?.sessionName && !masterIdentityChecked;

  useEffect(() => {
    if (resumeTarget?.sessionName) {
      setMasterIdentityChecked(true);
      return;
    }
    const uid = localStorage.getItem("userID");
    if (!uid) {
      setMasterIdentityChecked(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("https://xianhefairy.com/LotusFairy_API/api/profile/GetAll");
        if (!res.ok) throw new Error(`profile fetch failed: ${res.status}`);
        const data = await res.json();
        const numericId = Number(uid);
        const matched = (Array.isArray(data) ? data : []).find((p) => Number(p.ID) === numericId);
        const name = matched && (matched.DisplayName || matched.FirstName || matched.EmpCode);
        if (!cancelled && name) {
          console.debug("[ZoomTest] resuming master's personal room", name);
          setMasterIdentity({ sessionName: name, isHost: true, userName: name });
        }
      } catch (e) {
        console.warn("[ZoomTest] master identity lookup failed", e);
      } finally {
        if (!cancelled) setMasterIdentityChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Writes/clears the "live call" record the guest's waiting lobby polls for
  // via Booking/getCall, keyed by CHANNEL = the Zoom session name.
  // STATUS 0 = call live, STATUS 1 = call ended. `keepalive` lets the request
  // outlive the page so the "ended" POST still lands when it's fired from the
  // pagehide fallback below (host closes the tab / refreshes without hitting
  // Leave).
  const postOrEndBookingZoom = async (activeSessionName, status) => {
    if (!bookingState || bookingParticipants.length === 0) return;
    for (const p of bookingParticipants) {
      try {
        const body = JSON.stringify({
          EMP_CODE: bookingState.masCode,
          BUID: p.bookingUid,
          BOOKING_DATE: bookingState.bookingDate,
          CUSTOMER_ID: p.custID,
          CHANNEL: activeSessionName,
          CHANNEL_TOKEN: "",
          ROOM: "Normal",
          ROOM_TOKEN: "",
          STATUS: status,
        });
        // TEMP DEBUG — logged as a string so STATUS isn't hidden in a
        // collapsed object; remove once the status update is confirmed.
        console.log("[ZoomAppointment] PostBooking request body:", body);
        const res = await fetch("https://xianhefairy.com/LotusFairy_API/api/member/PostBooking", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          body,
        });
        const text = await res.text().catch(() => "(unreadable)");
        console.log("[ZoomAppointment] PostBooking response:", res.status, text);
      } catch (e) {
        console.warn("[ZoomTest] postOrEndBookingZoom failed for participant", p, e);
      }
    }
  };

  const [sdkKey, setSdkKey] = useState(ZOOM_CONFIG.sdkKey);
  const [sdkSecret, setSdkSecret] = useState(ZOOM_CONFIG.sdkSecret);
  const [sessionName, setSessionName] = useState("");
  const [userName, setUserName] = useState("Guest");
  const [isHost, setIsHost] = useState(false);
  const [joinedViaLink, setJoinedViaLink] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const [connecting, setConnecting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");

  const [micOn, setMicOn] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [sharingScreen, setSharingScreen] = useState(false);
  const [remoteSharing, setRemoteSharing] = useState(false);
  // "presenting" = I started the whiteboard; "viewing" = someone else did and I'm watching it.
  const [whiteboardState, setWhiteboardState] = useState("off"); // 'off' | 'presenting' | 'viewing'
  // True whenever a *peer* is presenting a whiteboard — purely for labeling
  // the shared-content tile correctly and hiding the (possibly broken-blank,
  // see nudgeWhiteboardResize) widget tile once that copy has attached.
  // NOT used to trigger any restart/recovery logic — see the note on
  // nudgeWhiteboardResize about why that was actively harmful.
  const [remoteWhiteboardActive, setRemoteWhiteboardActive] = useState(false);
  // Cloud recording — host-only, saved to Zoom's cloud via the SDK's recording client.
  const [recordingState, setRecordingState] = useState("Stopped"); // Zoom's RecordingStatus values
  const [recordingBusy, setRecordingBusy] = useState(false); // true while a start/stop/pause/resume call is in flight
  // The state we've asked Zoom to move to, shown in the UI immediately so the
  // button reacts on click even though Zoom's cloud recorder can take several
  // seconds to actually get there. Cleared when "recording-change" confirms
  // the real state (or the fallback timeout fires).
  const [pendingRecordingState, setPendingRecordingState] = useState(null);

  const [remoteUsers, setRemoteUsers] = useState([]);
  // Bumped each time a failover finishes (Reconnecting -> Connected) so the
  // roster-sync effect re-runs and re-attaches remote video/share — see the
  // connection-change handler in handleJoin.
  const [reconnectCount, setReconnectCount] = useState(0);
  // userId of the remote participant currently blown up to whiteboard size in
  // the right panel (null = nobody enlarged, normal layout).
  const [expandedRemoteUserId, setExpandedRemoteUserId] = useState(null);

  // Live compass heading (degrees) the customer's device streams over the
  // Zoom command channel — see the "compass" branch of the
  // command-channel-message handler in handleJoin. The master runs on
  // desktop web with no magnetometer, so this is display-only.
  const [remoteHeading, setRemoteHeading] = useState(null);
  const [remoteHeadingStale, setRemoteHeadingStale] = useState(false);
  const compassStaleTimerRef = useRef(null);
  const compassHideTimerRef = useRef(null);

  // Which tile (if any) *this* client has blown up to full screen —
  // 'whiteboard' | 'spotlight' | null. Local to each side (host and
  // participant each control their own view independently). The tile's
  // wrapper DOM node itself is what's put into the browser's Fullscreen API
  // (see toggleTileFullscreen), so Zoom's widget stays mounted in place
  // rather than being torn down and remounted.
  const [fullscreenTile, setFullscreenTile] = useState(null);

  const [isMobile, setIsMobile] = useState(false);
  // Viewport size (px) tracked on mobile. Height: so the whiteboard tile can
  // be given a concrete pixel height instead of a flex-grow / vh box. Width:
  // so the whiteboard widget can be mounted into a fixed WB_FORCE_WIDTH-wide
  // container and CSS-scaled down to fit — Zoom's whiteboard widget switches
  // to a broken "narrow" layout (drawing canvas never paints, see
  // nudgeWhiteboardResize) below an internal width breakpoint, and rendering
  // it at a desktop width then scaling is the only way found to keep it on
  // its working layout on a real phone.
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight || 0 : 0,
  );
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth || 0 : 0,
  );
  // Client compass mirror: ~28% of the viewport width, clamped so it's
  // never a speck on a huge monitor nor oversized on a small phone.
  const compassSize = Math.max(150, Math.min(340, Math.round(viewportWidth * 0.28)));

  useEffect(() => {
    const handleResize = () => {
      try {
        const w = window.innerWidth || 0;
        const h = window.innerHeight || 0;
        setIsMobile(w <= 768 && h >= w);
        setViewportHeight(h);
        setViewportWidth(w);
      } catch (e) {
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // Measured height (px) of the mobile user-tile strip, so the whiteboard tile
  // below it can be sized to exactly the leftover space above the fixed
  // control bar — see viewportHeight's comment for why the board needs a
  // concrete pixel height on mobile.
  const [leftPanelHeight, setLeftPanelHeight] = useState(0);
  const leftPanelRef = useRef(null);

  // Concrete pixel height for the whiteboard tile on mobile: whatever's left
  // between the user-tile strip and the fixed control bar (~104px incl. the
  // safe-area inset). Clamped so the board never collapses below something
  // usable even on a short viewport — a smaller-but-present board beats
  // Zoom's zero-height blank one.
  const CONTROL_BAR_RESERVE = 104;
  const mobileBoardHeight = Math.max(
    200,
    viewportHeight -
      (leftPanelHeight || Math.round(viewportHeight * 0.3)) -
      CONTROL_BAR_RESERVE,
  );

  // Mobile whiteboard: mount Zoom's widget into a container this many CSS px
  // wide (a "desktop" width, past Zoom's narrow-layout breakpoint) and scale
  // the whole thing down so it visually fits the phone. The board is
  // view-only for the mobile participant anyway (viewOnlyOverlay), so the
  // shrunk toolbar not being tappable doesn't matter.
  const WB_FORCE_WIDTH = 900;
  const mobileWbTileWidth = Math.max(0, viewportWidth - 26); // right panel padding + tile border
  const mobileWbScale = isMobile
    ? Math.min(1, (mobileWbTileWidth || WB_FORCE_WIDTH) / WB_FORCE_WIDTH)
    : 1;

  const clientRef = useRef(null);
  const streamRef = useRef(null);
  const whiteboardClientRef = useRef(null);
  const whiteboardContainerRef = useRef(null);
  const whiteboardBusyRef = useRef(false);
  // True while Zoom is failing this client over to a new connection (see the
  // connection-change handler in handleJoin). Whiteboard calls made in that
  // window fail with IMPROPER_MEETING_STATE, so the self-heal skips them.
  const reconnectingRef = useRef(false);
  // Wrapper elements the master can blow up to full screen via the browser's
  // Fullscreen API — see fullscreenTile/toggleTileFullscreen below.
  const whiteboardTileRef = useRef(null);
  const spotlightTileRef = useRef(null);
  const recordingClientRef = useRef(null);
  // Safety net in case the "recording-change" event never arrives (dropped
  // connection, etc.) — without this, recordingBusy could get stuck true
  // forever and the button would look permanently frozen.
  const recordingBusyTimeoutRef = useRef(null);
  // Latest values the pagehide fallback needs, mirrored into a ref so the
  // one-time listener never closes over a stale render. endSignalSentRef
  // guards against sending STATUS 1 twice (e.g. Leave button then pagehide).
  const leaveSignalRef = useRef({ isHost: false, joined: false, sessionName: "" });
  const endSignalSentRef = useRef(false);
  // Mirrors the `cameraOn` state, but readable from inside a setTimeout
  // callback without closing over a stale render — see
  // scheduleIOSSafariSelfViewRecovery.
  const cameraOnRef = useRef(false);
  const iosSelfViewRecoveryTimeoutRef = useRef(null);
  // Zoom Video SDK 2.x renders video into <video-player-container> web
  // components via attachVideo()/detachVideo() — canvas-based renderVideo()
  // is deprecated and throws INVALID_OPERATION on most Chromium setups
  // (it only works when the page has SharedArrayBuffer via COOP/COEP headers).
  const localVideoContainerRef = useRef(null);
  const screenCanvasRef = useRef(null);
  const remoteVideoContainerRefs = useRef(new Map()); // userId -> <video-player-container> element
  // The big "spotlight" <video-player-container> in the right panel, mounted
  // only while a remote participant is enlarged to whiteboard size (see
  // expandedRemoteUserId).
  const expandedRemoteVideoContainerRef = useRef(null);
  // userId -> the <video-player-container> element we currently have that
  // user's video attached into. Lets syncRemoteVideos tell "needs attaching"
  // from "already attached, leave it" AND "attached, but into the wrong tile
  // now" (the small left-panel tile vs. the big spotlight tile).
  const attachedVideoContainersRef = useRef(new Map());
  // <video-player-container> that renders whoever else is sharing — a real
  // screen share OR a whiteboard (Zoom presents the board over the same
  // underlying share pipeline as a screen share). Uses attachShareView, not
  // the deprecated canvas-based startShareView — see applyShareView's comment
  // for why that matters.
  const shareViewContainerRef = useRef(null);
  // userId whose share we currently have attached into shareViewContainerRef
  // (null = none).
  const attachedShareUserRef = useRef(null);
  // Serializes applyShareView so the roster-sync effect and the
  // `active-share-change` event can't both attach/detach at once.
  const shareViewBusyRef = useRef(false);
  // Last-seen bVideoOn per userId — lets the roster-sync effect tell "someone's
  // camera actually flipped" apart from an unrelated user-updated (mic, etc.),
  // so reattachWhiteboardIfNeeded only runs when it's plausibly relevant.
  const prevVideoOnRef = useRef(new Map());

  const setRemoteVideoContainerRef = (userId) => (el) => {
    if (el) remoteVideoContainerRefs.current.set(userId, el);
    else remoteVideoContainerRefs.current.delete(userId);
  };

  // If this page was opened from a "Copy meeting link" URL, the session was
  // created by someone else — join as a participant, not the master.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sess = params.get(SESSION_PARAM);
    if (sess) {
      setSessionName(sess);
      setIsHost(false);
      setJoinedViaLink(true);
    }
  }, []);

  // Zoom's whiteboard widget (loaded from their CDN at runtime, not
  // something this package exposes an API to configure) shows its own
  // "welcome" dialog for viewers, closable only via its own X or the Escape
  // key. A single delayed Escape press wasn't enough — Zoom can (re)show it
  // more than once per viewing session (e.g. after a reconnect, or simply
  // later than any fixed timeout covers on a slow connection), so instead
  // of guessing at timing, an effect below keeps quietly pressing Escape
  // on an interval for as long as a participant is viewing the board,
  // closing the dialog again every time it reappears.
  const pressEscapeKey = () => {
    document.dispatchEvent(
      new KeyboardEvent("keydown", { key: "Escape", code: "Escape", keyCode: 27, which: 27, bubbles: true }),
    );
  };

  const refreshUserList = () => {
    const client = clientRef.current;
    if (!client) return;
    const all = client.getAllUser() || [];
    const currentUserId = client.getCurrentUserInfo()?.userId;
    setRemoteUsers(all.filter((u) => u.userId !== currentUserId));
  };

  // Attaches/detaches remote video to match each participant's actual
  // bVideoOn, rather than reacting to a one-shot Start/Stop event — see the
  // comment where the old peer-video-state-change listener used to be, in
  // handleJoin. Re-run any time the roster changes (useEffect below), so a
  // participant whose video was already on before this client caught up
  // still gets rendered, not just future toggles.
  const syncRemoteVideos = async () => {
    const stream = streamRef.current;
    if (!stream) return;
    const currentIds = new Set(remoteUsers.map((u) => u.userId));
    for (const [id, container] of Array.from(attachedVideoContainersRef.current.entries())) {
      if (currentIds.has(id)) continue;
      // Participant left the meeting. Just forgetting them isn't enough: their
      // <video-player> element stays in the tile and keeps showing the last
      // frame it rendered, so tear it down explicitly.
      attachedVideoContainersRef.current.delete(id);
      try {
        const els = await stream.detachVideo(id);
        (Array.isArray(els) ? els : [els]).forEach((e) => e?.remove?.());
      } catch (e) {}
      // detachVideo can fail/return nothing for a user who's already gone, so
      // also empty the container — unless someone else is now attached there.
      const reused = Array.from(attachedVideoContainersRef.current.values()).includes(container);
      if (container && !reused) container.replaceChildren();
    }
    for (const u of remoteUsers) {
      // Where this user's video should live right now: the big spotlight tile
      // if they're the enlarged one (and it's mounted), otherwise their small
      // left-panel tile.
      const wantExpanded = u.userId === expandedRemoteUserId;
      const container =
        wantExpanded && expandedRemoteVideoContainerRef.current
          ? expandedRemoteVideoContainerRef.current
          : remoteVideoContainerRefs.current.get(u.userId);
      const attachedContainer = attachedVideoContainersRef.current.get(u.userId) || null;
      try {
        if (u.bVideoOn && container) {
          // Already rendered exactly where we want it — nothing to do.
          if (attachedContainer === container) continue;
          // Either a first attach, or the target tile changed (small <-> big):
          // Zoom only renders a given user into one element at a time, so tear
          // the old attach down before re-attaching into the new container.
          if (attachedContainer) {
            try {
              const prev = await stream.detachVideo(u.userId);
              (Array.isArray(prev) ? prev : [prev]).forEach((e) => e?.remove?.());
            } catch (e) {}
          }
          const el = await stream.attachVideo(
            u.userId,
            wantExpanded ? VideoQuality.Video_720P : VideoQuality.Video_360P,
          );
          if (!el || !(el instanceof HTMLElement)) {
            // attachVideo resolves to an ExecutedFailure object (not an
            // element) on failure — replaceChildren() on that would throw.
            console.warn("[ZoomTest] attachVideo failed for user", u.userId, el);
            attachedVideoContainersRef.current.delete(u.userId);
            continue;
          }
          // Zoom's <video-player> renders frames through its own canvas
          // pipeline and only honors object-fit when set directly on the
          // element's style (a stylesheet-only rule can be missed
          // depending on when it's first read) — without this it
          // defaults to cropping/zooming instead of showing the full frame.
          el.style.width = "100%";
          el.style.height = "100%";
          el.style.objectFit = "contain";
          container.replaceChildren(el);
          attachedVideoContainersRef.current.set(u.userId, container);
        } else if (!u.bVideoOn && attachedContainer) {
          const els = await stream.detachVideo(u.userId);
          (Array.isArray(els) ? els : [els]).forEach((e) => e?.remove?.());
          attachedVideoContainersRef.current.delete(u.userId);
        }
      } catch (e) {
        console.warn("[ZoomTest] syncRemoteVideos failed for user", u.userId, e);
      }
    }
  };

  // Confirmed by inspecting the live DOM on a broken mobile session: Zoom's
  // widget mounts its full UI (header, toolbar, collaborator list all
  // present and correct) but its own internal canvas ends up with
  // `.canvas-container { width: 474px; height: 0px }` — width computed fine,
  // height computed to zero. So this isn't a missing/unmounted iframe, it's
  // Zoom's own layout math landing on a zero-height box for the actual
  // drawing surface, specifically on the narrow (mobile) layout.
  //
  // A stop+start restart on a blank canvas WAS tried here and made things
  // worse: testing caught Zoom's own SDK throwing
  // "Cannot read properties of undefined (reading 'renderEngine')" from
  // inside its own fitToScreen/applyPreviousViewport init chain — a crash
  // that happens on Zoom's side, before this code even runs — and then a
  // restart attempted on top of that crashed state failed with
  // "Current presenter: none" and left the widget completely torn down
  // (no header, no toolbar, nothing), which is strictly worse than the
  // original blank-but-intact canvas. This is the exact "back-to-back
  // stop/start corrupts the render engine" failure already documented on
  // reattachWhiteboardIfNeeded below — do not reintroduce it here.
  //
  // So this only does the safe part: jiggle the container's real *height*
  // (a synthetic `window.resize` event and jiggling *width* were both tried
  // and confirmed ineffective) to give any ResizeObserver Zoom has on it a
  // real change to react to, without ever tearing the widget down ourselves.
  const nudgeWhiteboardResize = () => {
    const jiggle = () => {
      const el = whiteboardContainerRef.current;
      if (!el) return;
      const prevHeight = el.style.height;
      el.style.height = "calc(100% - 1px)";
      void el.offsetHeight; // force a synchronous layout so the shrink actually takes effect
      requestAnimationFrame(() => {
        el.style.height = prevHeight || "100%";
      });
    };
    try {
      window.dispatchEvent(new Event("resize"));
    } catch (e) {}
    jiggle();
    setTimeout(jiggle, 300);
    setTimeout(jiggle, 1200);
  };

  // Renders whoever is currently sharing their real screen — via
  // attachShareView into shareViewContainerRef, not the deprecated canvas-based
  // startShareView (which fails with INVALID_PARAMETERS the moment its canvas
  // is display:none, which it is right up until this call flips it visible).
  // attachShareView returns its own element the same way attachVideo already
  // does for camera video elsewhere in this file, so there's no canvas-sizing
  // race to fall into.
  //
  // NOTE: this does NOT cover the whiteboard. Confirmed by testing —
  // attachShareView(presenterId) while a board is up fails with
  // INVALID_PARAMETERS "Target user is not sharing" — the whiteboard does not
  // ride this share pipeline in this SDK version, despite older comments in
  // this file having assumed it did. The whiteboard viewer's own
  // startWhiteboardView() widget is the only way to see a board; see
  // nudgeWhiteboardResize for the actual mobile-blank-board fix.
  const applyShareView = async (sharerUserId) => {
    const stream = streamRef.current;
    const container = shareViewContainerRef.current;
    if (!stream || !container) return;
    if (shareViewBusyRef.current) return;
    const attachedId = attachedShareUserRef.current;
    if (sharerUserId === attachedId) return;
    shareViewBusyRef.current = true;
    try {
      if (attachedId != null) {
        try {
          const els = await stream.detachShareView(attachedId);
          (Array.isArray(els) ? els : [els]).forEach((e) => e?.remove?.());
        } catch (e) {}
        attachedShareUserRef.current = null;
        setRemoteSharing(false);
      }
      if (sharerUserId != null) {
        const el = await stream.attachShareView(sharerUserId);
        if (!el || !(el instanceof HTMLElement)) {
          // attachShareView resolves to an ExecutedFailure object (not an
          // element) on failure — replaceChildren() on that would throw.
          console.warn("[ZoomTest] attachShareView failed for user", sharerUserId, el);
        } else {
          el.style.width = "100%";
          el.style.height = "100%";
          el.style.objectFit = "contain";
          container.replaceChildren(el);
          attachedShareUserRef.current = sharerUserId;
          setRemoteSharing(true);
        }
      }
    } catch (e) {
      console.warn("[ZoomTest] applyShareView failed", e);
    } finally {
      shareViewBusyRef.current = false;
    }
  };

  // Fold a new compass reading from the customer into local state. The
  // customer resends at least every ~2s while their compass is open, so a
  // gap means their signal dropped: fade the widget after 6s, drop it
  // entirely after 30s. A null `deg` (customer closed their compass) hides
  // it immediately.
  const ingestRemoteHeading = (deg) => {
    if (compassStaleTimerRef.current) clearTimeout(compassStaleTimerRef.current);
    if (compassHideTimerRef.current) clearTimeout(compassHideTimerRef.current);
    const n = Number(deg);
    if (deg == null || Number.isNaN(n)) {
      setRemoteHeading(null);
      setRemoteHeadingStale(false);
      return;
    }
    setRemoteHeading(((n % 360) + 360) % 360);
    setRemoteHeadingStale(false);
    compassStaleTimerRef.current = setTimeout(() => setRemoteHeadingStale(true), 6000);
    compassHideTimerRef.current = setTimeout(() => {
      setRemoteHeading(null);
      setRemoteHeadingStale(false);
    }, 30000);
  };

  const handleJoin = async ({
    hostFlag,
    sessionNameOverride,
    userNameOverride,
    sdkKeyOverride,
    sdkSecretOverride,
  } = {}) => {
    const effectiveHost = hostFlag ?? isHost;
    const effectiveSession = (sessionNameOverride ?? sessionName).trim();
    // Overrides (not just reading the state vars directly) matter here: a
    // caller that just called setUserName()/setSdkKey() right before this
    // would otherwise see the *previous* render's stale value, since state
    // updates aren't applied synchronously within the same function call.
    const effectiveUserName = userNameOverride ?? userName;
    const effectiveSdkKey = sdkKeyOverride ?? sdkKey;
    const effectiveSdkSecret = sdkSecretOverride ?? sdkSecret;

    if (!effectiveSession) {
      setError("Enter or generate a session name first.");
      return;
    }

    setError("");
    setConnecting(true);
    try {
      const signature = await buildZoomSignature({
        sdkKey: effectiveSdkKey,
        sdkSecret: effectiveSdkSecret,
        sessionName: effectiveSession,
        role: effectiveHost ? ROLE_HOST : ROLE_PARTICIPANT,
      });

      const client = ZoomVideo.createClient();
      clientRef.current = client;
      await client.init("en-US", "Global", { patchJsMedia: true });

      client.on("user-added", refreshUserList);
      client.on("user-removed", refreshUserList);
      client.on("user-updated", refreshUserList);

      // A network blip (or a decode failure on an incoming stream) makes Zoom
      // silently fail over to a new connection. Other participants see this
      // user leave and immediately rejoin. Log it so a "the other side
      // dropped" report can be matched to it.
      client.on("connection-change", async (payload) => {
        console.warn("[ZoomTest] connection-change", payload.state, payload.reason);
        const wasReconnecting = reconnectingRef.current;
        reconnectingRef.current = payload.state === "Reconnecting";
        if (!wasReconnecting || payload.state !== "Connected") return;
        // The failover rebuilt the media connection underneath the
        // <video-player> elements we'd attached, so they can sit frozen on
        // their last frame — yet attachedVideoContainersRef still says
        // "attached", so syncRemoteVideos would skip them. Detach and forget
        // them all, then let the roster-sync effect attach fresh ones.
        const stream = streamRef.current;
        for (const id of Array.from(attachedVideoContainersRef.current.keys())) {
          try {
            const els = await stream?.detachVideo(id);
            (Array.isArray(els) ? els : [els]).forEach((e) => e?.remove?.());
          } catch (e) {}
        }
        attachedVideoContainersRef.current.clear();
        if (attachedShareUserRef.current != null) {
          try {
            const els = await stream?.detachShareView(attachedShareUserRef.current);
            (Array.isArray(els) ? els : [els]).forEach((e) => e?.remove?.());
          } catch (e) {}
          attachedShareUserRef.current = null;
          setRemoteSharing(false);
        }
        refreshUserList();
        setReconnectCount((n) => n + 1);
      });

      // peer-video-state-change used to drive remote video attach/detach
      // directly, but it's a one-shot event: a client that wasn't
      // subscribed yet (or whose container wasn't mounted yet) when it
      // fired — e.g. the other side's camera was already on before this
      // client finished joining — would simply never see that user's video,
      // with no retry. syncRemoteVideos (driven by the useEffect below,
      // keyed on remoteUsers/bVideoOn) replaces it: it's re-run on every
      // roster update, so it both reacts to changes and catches up on
      // anything already true by the time this client was ready.

      client.on("recording-change", (payload) => {
        setRecordingState(payload.state);
        // This event is the actual confirmation that Zoom's cloud recorder
        // finished starting/stopping/pausing — clear the busy/pending flag
        // here rather than as soon as the start/stop call's promise
        // resolves, since that promise only confirms the *request* was
        // sent, not that recording actually changed state yet (that leg
        // alone commonly takes several seconds on Zoom's side).
        if (recordingBusyTimeoutRef.current) {
          clearTimeout(recordingBusyTimeoutRef.current);
          recordingBusyTimeoutRef.current = null;
        }
        setPendingRecordingState(null);
        setRecordingBusy(false);
      });

      client.on("individual-recording-change", async (payload) => {
        // Some accounts require each participant to consent before their
        // stream is included in the cloud recording.
        if (payload.state !== "Ask") return;
        const rec = recordingClientRef.current;
        if (!rec) return;
        const consent = window.confirm(
          "The host is cloud-recording this session. Do you consent to being recorded?",
        );
        try {
          if (consent) await rec.acceptIndividualRecording();
          else await rec.declineIndividualRecording();
        } catch (e) {
          console.warn("[ZoomTest] recording consent response failed", e);
        }
      });

      client.on("peer-whiteboard-state-change", async (payload) => {
        // Zoom broadcasts this event to the presenter too — without this
        // check the host's own client would race startWhiteboardView()
        // against its own startWhiteboardScreen() on the same container.
        const isMe = payload.userId === client.getCurrentUserInfo()?.userId;
        const wb = whiteboardClientRef.current;
        if (isMe) return;
        // Set regardless of whether the widget below succeeds — on some
        // mobile sessions it doesn't (see nudgeWhiteboardResize), but the
        // shared-content tile (applyShareView, driven by the roster's
        // sharerOn flag) still needs to know a board is up so it can label
        // itself "Whiteboard" instead of "Screen share".
        setRemoteWhiteboardActive(payload.action === "Start");
        if (!wb || !whiteboardContainerRef.current) return;
        try {
          if (payload.action === "Start") {
            await wb.startWhiteboardView(whiteboardContainerRef.current, payload.userId);
            setWhiteboardState("viewing");
            pressEscapeKey();
            nudgeWhiteboardResize();
          } else if (payload.action === "Stop") {
            await wb.stopWhiteboardView();
            setWhiteboardState("off");
          }
        } catch (e) {
          console.warn("[ZoomTest] whiteboard view failed", e);
        }
      });

      client.on("command-channel-message", (payload) => {
        let msg;
        try {
          msg = JSON.parse(payload.text);
        } catch {
          return; // not one of ours
        }
        // TEMP DEBUG — remove once the compass mirror is confirmed working.
        console.log("[ZoomAppointment] command-channel message received", msg, payload);
        if (msg.t === "compass") ingestRemoteHeading(msg.deg);
      });

      client.on("active-share-change", (payload) => {
        const isMe = payload.userId === client.getCurrentUserInfo()?.userId;
        if (isMe) return;
        // Also handled by the roster-sync effect (keyed on `sharerOn`) — this
        // event just reacts a touch faster for the common case.
        // applyShareView is idempotent, so the two can't fight.
        applyShareView(payload.state === "Active" ? payload.userId : null);
      });

      await client.join(effectiveSession, signature, effectiveUserName);

      const stream = client.getMediaStream();
      streamRef.current = stream;
      whiteboardClientRef.current = client.getWhiteboardClient();
      recordingClientRef.current = client.getRecordingClient();

      // Lock the whiteboard to the host so participants can only ever view
      // it — they can't start their own board or grab drawing control away
      // from the host's active one. setWhiteboardPermission only exists in
      // newer Video SDK builds (it's absent in 2.4.5), so feature-detect it
      // rather than letting the call throw — participants are already gated
      // to view-only by the UI (isHost checks + viewOnlyOverlay) regardless.
      const wbClient = whiteboardClientRef.current;
      if (effectiveHost && typeof wbClient?.setWhiteboardPermission === "function") {
        try {
          await wbClient.setWhiteboardPermission({
            initiatePermission: WhiteboardInitiatePermissionCode.HostOnly,
            sharePermission: WhiteboardSharePermissionCode.LockShare,
          });
        } catch (e) {
          console.warn("[ZoomTest] lock whiteboard permission failed", e);
        }
      }

      setIsHost(effectiveHost);
      setSessionName(effectiveSession);
      setUserName(effectiveUserName);
      setSdkKey(effectiveSdkKey);
      setSdkSecret(effectiveSdkSecret);
      if (effectiveHost) {
        setMeetingLink(buildMeetingLink(effectiveSession));
        endSignalSentRef.current = false;
        postOrEndBookingZoom(effectiveSession, 0);
      }

      writeActiveMeeting({
        sessionName: effectiveSession,
        isHost: effectiveHost,
        userName: effectiveUserName,
        sdkKey: effectiveSdkKey,
        sdkSecret: effectiveSdkSecret,
      });

      setJoined(true);
      refreshUserList();
    } catch (err) {
      console.error("[ZoomTest] join failed", err);
      setError(err?.message || String(err));
      // Same singleton reasoning as handleLeave: don't leave this attempt's
      // listeners attached for the next one.
      clientRef.current = null;
      try { await ZoomVideo.destroyClient(); } catch (e) {}
    } finally {
      setConnecting(false);
    }
  };

  // Skip the manual "Create/Join Meeting" sandbox entirely when we already
  // know which meeting to be in — a fresh booking-flow navigation (e.g. the
  // master's "Meet Now" button), whatever meeting sessionStorage says was
  // active a moment ago, or (once the async identity check above resolves)
  // a logged-in master's own personal room.
  const autoJoinAttemptedRef = useRef(false);
  useEffect(() => {
    if (autoJoinAttemptedRef.current) return;
    if (!resumeTarget?.sessionName) return; // includes: still resolving identity — effect re-fires once it settles
    autoJoinAttemptedRef.current = true;

    const isFreshBookingNav = !!bookingState?.sessionName;
    // Clear eagerly rather than after a failed attempt: if the stored
    // session has actually ended (host long gone, etc.) this avoids
    // re-attempting — and failing — the same resume on every subsequent
    // refresh. A successful join re-writes it right away.
    if (!isFreshBookingNav) clearActiveMeeting();

    (async () => {
      if (!isFreshBookingNav) {
        // A page refresh doesn't cleanly close the previous Zoom connection
        // — the browser just drops it, and Zoom's server notices via its
        // own heartbeat timeout rather than instantly. Rejoining the moment
        // this tab reloads risks racing that teardown; a short pause first
        // avoids it. Not needed for a genuinely fresh booking-flow join,
        // since there's no prior connection to race.
        await new Promise((r) => setTimeout(r, 1200));
      }
      handleJoin({
        hostFlag: !!resumeTarget.isHost,
        sessionNameOverride: resumeTarget.sessionName,
        userNameOverride: resumeTarget.userName,
        sdkKeyOverride: resumeTarget.sdkKey,
        sdkSecretOverride: resumeTarget.sdkSecret,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeTarget, masterIdentityChecked]);

  const handleLeave = async () => {
    try {
      if (isHost && !endSignalSentRef.current) {
        endSignalSentRef.current = true;
        await postOrEndBookingZoom(sessionName, 1);
      }
      const stream = streamRef.current;
      const client = clientRef.current;
      if (stream) {
        try { if (micOn) await stream.stopAudio(); } catch (e) {}
        try {
          if (cameraOn) {
            const currentUserId = client?.getCurrentUserInfo()?.userId;
            if (currentUserId != null) await stream.detachVideo(currentUserId);
            await stream.stopVideo();
          }
        } catch (e) {}
        try { if (sharingScreen) await stream.stopShareScreen(); } catch (e) {}
        try {
          if (attachedShareUserRef.current != null) await stream.detachShareView(attachedShareUserRef.current);
        } catch (e) {}
      }
      try {
        const wb = whiteboardClientRef.current;
        if (wb) {
          if (whiteboardState === "presenting") await wb.stopWhiteboardScreen();
          else if (whiteboardState === "viewing") await wb.stopWhiteboardView();
        }
      } catch (e) {}
      try {
        const rec = recordingClientRef.current;
        if (isHost && rec && (recordingState === "Recording" || recordingState === "Paused")) {
          await rec.stopCloudRecording();
        }
      } catch (e) {}
      if (client) {
        await client.leave();
        // createClient() hands back the same singleton every time, so without
        // this every listener handleJoin registers (command-channel,
        // whiteboard, share, recording...) piles up again on the next Meet
        // Now. destroyClient() drops the instance and all its listeners.
        await ZoomVideo.destroyClient();
      }
    } catch (e) {
      console.warn("[ZoomTest] leave error", e);
    } finally {
      if (recordingBusyTimeoutRef.current) {
        clearTimeout(recordingBusyTimeoutRef.current);
        recordingBusyTimeoutRef.current = null;
      }
      if (iosSelfViewRecoveryTimeoutRef.current) {
        clearTimeout(iosSelfViewRecoveryTimeoutRef.current);
        iosSelfViewRecoveryTimeoutRef.current = null;
      }
      if (compassStaleTimerRef.current) {
        clearTimeout(compassStaleTimerRef.current);
        compassStaleTimerRef.current = null;
      }
      if (compassHideTimerRef.current) {
        clearTimeout(compassHideTimerRef.current);
        compassHideTimerRef.current = null;
      }
      reconnectingRef.current = false;
      clientRef.current = null;
      streamRef.current = null;
      whiteboardClientRef.current = null;
      recordingClientRef.current = null;
      attachedVideoContainersRef.current.clear();
      attachedShareUserRef.current = null;
      prevVideoOnRef.current.clear();
      cameraOnRef.current = false;
      setJoined(false);
      setMicOn(false);
      setCameraOn(false);
      setSharingScreen(false);
      setRemoteSharing(false);
      setWhiteboardState("off");
      setRemoteWhiteboardActive(false);
      setRecordingState("Stopped");
      setRecordingBusy(false);
      setPendingRecordingState(null);
      setRemoteUsers([]);
      setExpandedRemoteUserId(null);
      setRemoteHeading(null);
      setRemoteHeadingStale(false);
      setMeetingLink("");
      clearActiveMeeting();
      // Whoever arrived here from a booking flow (master's "Meet Now" or a
      // guest pulled in from the waiting lobby) should be sent back to
      // wherever that was, not left staring at the "Joining…" screen —
      // that screen only shows because bookingState.sessionName is still set.
      if (bookingState?.sessionName) navigate(-1);
    }
  };

  const handleCreateMeeting = () => {
    const finalSession = sessionName.trim() || generateSessionName();
    handleJoin({ hostFlag: true, sessionNameOverride: finalSession });
  };

  const handleJoinMeeting = () => {
    if (!sessionName.trim()) {
      setError("Enter a session name, or open a meeting link shared by the master.");
      return;
    }
    handleJoin({ hostFlag: false, sessionNameOverride: sessionName });
  };

  const copyMeetingLink = () => {
    if (!meetingLink) return;
    navigator.clipboard
      .writeText(meetingLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch((err) => console.error("Failed to copy meeting link", err));
  };

  // Anyone (host or participant) can press the whiteboard or the
  // enlarged/spotlight camera tile to blow it up to full screen via the
  // browser's Fullscreen API, press again (or Esc) to shrink back. Purely a
  // local, client-side view change — it doesn't touch anything shared with
  // the other side. Fullscreening the tile's existing wrapper node — rather
  // than duplicating it into a separate overlay — keeps Zoom's mounted
  // whiteboard/video widget exactly where it is, so nothing needs to be
  // re-attached or restarted.
  const toggleTileFullscreen = (el) => {
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        document.exitFullscreen();
      } else {
        el.requestFullscreen();
      }
    } catch (e) {
      console.warn("[ZoomTest] fullscreen toggle failed", e);
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const el = document.fullscreenElement;
      if (el === whiteboardTileRef.current) setFullscreenTile("whiteboard");
      else if (el === spotlightTileRef.current) setFullscreenTile("spotlight");
      else setFullscreenTile(null);
      if (el) nudgeWhiteboardResize();
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      // best-effort cleanup if the component unmounts mid-call
      // This closure is from the first render, so the handleLeave it sees has
      // isHost=false and would skip the STATUS 1 "call ended" signal on an
      // in-app navigation away (browser Back, a menu link); send it from
      // leaveSignalRef, which always holds the current values, like pagehide.
      const { isHost: host, joined: inCall, sessionName: name } = leaveSignalRef.current;
      if (host && inCall && !endSignalSentRef.current) {
        endSignalSentRef.current = true;
        postOrEndBookingZoom(name, 1);
      }
      if (clientRef.current) handleLeave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React's unmount cleanup above only runs on an in-app navigation — it never
  // fires when the host just closes the tab, refreshes, or the browser/OS
  // kills the page, which would leave the "live call" record stuck at STATUS 0
  // and guests seeing a dead meeting in the waiting lobby forever. pagehide
  // reliably fires in all of those cases; the keepalive fetch inside
  // postOrEndBookingZoom is what lets the request finish after the page is
  // gone. We deliberately do NOT use visibilitychange here — it also fires on
  // a plain tab switch / minimise, which would end the meeting out from under
  // a host who's only briefly looking elsewhere.
  useEffect(() => {
    leaveSignalRef.current = { isHost, joined, sessionName };
  });
  useEffect(() => {
    const onPageHide = (event) => {
      // persisted === true: page is going into the bfcache and may be
      // restored, so it isn't really ending. (A live Zoom RTC connection
      // makes the page bfcache-ineligible anyway, so in practice this is
      // always false while in a call — it's just belt-and-braces.)
      if (event.persisted) return;
      const { isHost: host, joined: inCall, sessionName: name } = leaveSignalRef.current;
      if (!host || !inCall || endSignalSentRef.current) return;
      endSignalSentRef.current = true;
      postOrEndBookingZoom(name, 1);
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zoom's whiteboard bundle throws a few benign errors of its own
  // ("premature EOF", "reading 'renderEngine'") from inside its internal
  // callbacks. They're filtered by an inline script in public/index.html —
  // it has to run before the dev-server overlay registers its listeners,
  // which no component-level listener can do.

  // Host: open a blank whiteboard automatically once whiteboardContainerRef
  // is actually mounted (right after setJoined(true) in handleJoin is too
  // early — React hasn't committed that render yet).
  // Participant: catch up if a presenter is already mid-session (e.g. they
  // joined a bit late and missed the "Start" event, which only fires for
  // changes after you're subscribed).
  useEffect(() => {
    if (!joined) return;
    if (isHost) {
      startPresenting();
      return;
    }
    const wb = whiteboardClientRef.current;
    const container = whiteboardContainerRef.current;
    if (!wb || !container) return;
    const presenter = wb.getWhiteboardPresenter?.();
    if (!presenter) return;
    // A board is already up — record that even if the widget below fails to
    // render it, so the shared-content tile can still label itself correctly.
    setRemoteWhiteboardActive(true);
    wb.startWhiteboardView(container, presenter.userId)
      .then(() => {
        setWhiteboardState("viewing");
        pressEscapeKey();
        nudgeWhiteboardResize();
      })
      .catch((e) => console.warn("[ZoomTest] whiteboard catch-up failed", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, isHost]);

  // Re-syncs remote video (attach whoever's bVideoOn we haven't rendered
  // yet, detach whoever turned it off) and self-heals the whiteboard any
  // time the roster changes — covers both a participant whose video was
  // already on before this client caught up, and a *remote* peer's camera
  // toggle knocking our own whiteboard view/presenting session out (see the
  // comments on syncRemoteVideos and reattachWhiteboardIfNeeded).
  useEffect(() => {
    if (!joined) return;
    syncRemoteVideos();

    // Render (or drop) a peer's real screen share to match the roster —
    // more reliable than depending solely on the one-shot `active-share-change`
    // event (mirrors syncRemoteVideos replacing the old peer-video-state-change).
    const sharer = remoteUsers.find((u) => u.sharerOn);
    applyShareView(sharer ? sharer.userId : null);

    const prev = prevVideoOnRef.current;
    let videoStateChanged = false;
    for (const u of remoteUsers) {
      if (prev.get(u.userId) !== u.bVideoOn) videoStateChanged = true;
    }
    prevVideoOnRef.current = new Map(remoteUsers.map((u) => [u.userId, u.bVideoOn]));
    if (videoStateChanged) reattachWhiteboardIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, remoteUsers, expandedRemoteUserId, reconnectCount]);

  // Drop the spotlight if the enlarged participant has left the meeting.
  useEffect(() => {
    if (expandedRemoteUserId == null) return;
    if (!remoteUsers.some((u) => u.userId === expandedRemoteUserId)) {
      setExpandedRemoteUserId(null);
    }
  }, [remoteUsers, expandedRemoteUserId]);

  // Zoom's whiteboard iframe sizes itself to its container on start and
  // doesn't reliably re-measure when the surrounding layout settles a beat
  // later (common on mobile, where the flex layout and on-screen keyboard /
  // toolbar shift things around after mount) — leaving the board blank or
  // clipped. Re-fire a resize whenever the layout mode flips or an orientation
  // change lands while a board is up, so Zoom repaints it at the right size.
  useEffect(() => {
    if (!joined || whiteboardState === "off") return;
    nudgeWhiteboardResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, whiteboardState, isMobile, mobileBoardHeight, mobileWbScale]);

  // Track the real rendered height of the mobile user-tile strip so
  // mobileBoardHeight (below) can hand the whiteboard tile exactly the space
  // left between it and the fixed control bar. A ResizeObserver rather than a
  // one-shot measure — the strip's height shifts as remote tiles come and go
  // and as the first video frames change each tile's intrinsic size.
  useEffect(() => {
    const el = leftPanelRef.current;
    if (!joined || !isMobile || !el || typeof ResizeObserver === "undefined") {
      setLeftPanelHeight(0);
      return;
    }
    const measure = () => setLeftPanelHeight(el.offsetHeight || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [joined, isMobile, remoteUsers.length]);

  // Keeps closing Zoom's viewer-only "welcome" dialog for as long as this
  // participant is looking at the board — see the comment on
  // pressEscapeKey() for why a one-off dismiss isn't reliable enough.
  useEffect(() => {
    if (isHost || whiteboardState !== "viewing") return;
    const id = setInterval(pressEscapeKey, 1000);
    return () => clearInterval(id);
  }, [isHost, whiteboardState]);

  // reattachWhiteboardIfNeeded (below) was originally only ever called right
  // after a camera toggle, because that's the one trigger the SDK's own
  // events told us about (see its comment for the mechanism: whiteboard
  // sharing rides the same pipeline as camera/screen video, so anyone's
  // camera flipping can silently drop it without any event firing). In
  // practice a presenter's strokes can also stop reaching a viewer (most
  // reported on Chrome so far) with no camera toggle involved at all — a
  // dropped share renegotiation, a backgrounded tab, etc. — which the
  // camera-triggered check alone can't catch. Poll the same self-heal check
  // on a timer for as long as a board is up, on both sides; it already
  // no-ops immediately unless it can tell something is actually broken
  // (empty viewer container / presenter status not InProgress), so this is
  // just a periodic safety net, not a periodic restart.
  useEffect(() => {
    if (!joined) return;
    if (whiteboardState !== "presenting" && whiteboardState !== "viewing") return;
    const id = setInterval(() => {
      reattachWhiteboardIfNeeded();
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, whiteboardState]);

  // A Zoom session doesn't end just because the host left it — it stays
  // open for anyone still connected, so a guest left behind would
  // otherwise be stuck alone in it with no indication the meeting is
  // over. This also covers a guest joining without a host ever having
  // been there in the first place: the waiting lobby (see
  // UpcomingConsultation.jsx's openWaitingLobby) polls Booking/getCall
  // only every 10s, so it can hand out a session name that was already
  // ended by the time the guest's client.join() actually lands. Either
  // way, treat "no host in the participant list" as "this meeting isn't
  // usable" and get the guest out automatically. The 4s grace period is
  // just to ride out the moment right after joining, before the host's
  // participant record is guaranteed to have shown up in remoteUsers.
  useEffect(() => {
    if (!joined || isHost) return;
    if (remoteUsers.some((u) => u.isHost)) return;
    const timer = setTimeout(() => {
      window.alert("The host isn't in this meeting — you'll be returned to the previous page.");
      handleLeave();
    }, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, isHost, remoteUsers]);

  const toggleMic = async () => {
    const stream = streamRef.current;
    if (!stream) return;
    try {
      if (micOn) {
        await stream.muteAudio();
      } else {
        // startAudio() is safe to call again if already started; Zoom SDK no-ops it
        await stream.startAudio();
        await stream.unmuteAudio();
      }
      setMicOn((v) => !v);
    } catch (e) {
      console.warn("[ZoomTest] toggleMic failed", e);
      setError(e?.message || String(e));
    }
  };

  // Whiteboard *presenting/viewing* is implemented by Zoom's SDK as a "share"
  // over the same underlying media pipeline as camera/screen-share (see the
  // Start_Whiteboard_Sharing / "Sharing Version" console logs that fire
  // whenever it starts). ANYONE's camera starting or stopping — not just
  // the local user's — can force that shared pipeline to renegotiate and
  // silently drop the board: a viewer's active view goes blank, or a host's
  // active presenting session stops actually rendering. Since nobody
  // actually called stop on it, peer-whiteboard-state-change (the only
  // thing that normally re-attaches a viewer, and the only thing that
  // normally clears whiteboardState for a host) never fires, so nothing
  // else notices. Called both right after this client's own camera toggle,
  // and from the roster-sync effect below whenever someone's bVideoOn
  // actually changes (covers a *remote* peer's camera breaking our board).
  const reattachWhiteboardIfNeeded = async () => {
    const wb = whiteboardClientRef.current;
    const container = whiteboardContainerRef.current;
    if (!wb || !container) return;
    // Guard against racing a manual toggleWhiteboard()/startPresenting()
    // click, which also mutates whiteboard share state on the same client.
    if (whiteboardBusyRef.current) return;
    // Mid-failover: the board isn't broken, the connection is. The 5s poll
    // will check again once it's back.
    if (reconnectingRef.current) return;
    try {
      if (isHost) {
        if (whiteboardState !== "presenting") return;
        // Only restart if the share actually dropped — restarting
        // unconditionally would reset the board's view for everyone on
        // every single video toggle in the room, not just when it's
        // actually broken.
        // Only a Closed board has actually dropped. Pending means Zoom is
        // still (re)initializing it — e.g. while a guest's camera coming on
        // renegotiates the share pipeline — and stopping it mid-init tears
        // the board down under the viewers (the start that follows then
        // fails with INSUFFICIENT_PRIVILEGES and the widget chokes decoding
        // the half-torn-down session: "RangeError: premature EOF").
        if (wb.getWhiteboardStatus() !== WhiteboardStatus.Closed) return;
        whiteboardBusyRef.current = true;
        // The SDK's own share state can be left thinking a screen share is
        // still active even though it stopped actually rendering (that's
        // the "dropped" case this whole function exists for) — calling
        // startWhiteboardScreen() again without stopping first then fails
        // with INVALID_OPERATION. Mirrors the stop-then-start already done
        // for viewers below.
        try {
          await wb.stopWhiteboardScreen();
        } catch (e) {}
        await wb.startWhiteboardScreen(container);
        nudgeWhiteboardResize();
      } else {
        if (whiteboardState !== "viewing") return;
        // Zoom doesn't expose a per-viewer "is my view still rendering"
        // status the way getWhiteboardStatus() does for a presenter — so use
        // the container's own DOM as the signal instead. If Zoom's widget
        // still has content painted into it, the view survived the camera
        // change and forcing a stop+start here would be an unnecessary
        // restart. That unconditional restart used to run on every single
        // camera toggle regardless of whether anything actually broke, and
        // the back-to-back stopWhiteboardView()/startWhiteboardView() call
        // is what was corrupting the widget's internal render engine —
        // INVALID_PARAMETERS from the immediate restart left the widget
        // half torn-down, and its own deferred resize/render callbacks then
        // threw against that broken state (the getVisibleAreaBBox /
        // renderEngine crashes), closing the board instead of recovering it.
        if (container.childElementCount > 0) return;
        const presenter = wb.getWhiteboardPresenter?.();
        if (!presenter) return;
        whiteboardBusyRef.current = true;
        try {
          await wb.stopWhiteboardView();
        } catch (e) {}
        // Give Zoom's teardown a beat to finish (including any queued
        // resize/render callbacks referencing the old canvas) before asking
        // it to initialize a new one in the same container.
        await new Promise((r) => setTimeout(r, 400));
        try {
          await wb.startWhiteboardView(container, presenter.userId);
          pressEscapeKey();
          nudgeWhiteboardResize();
        } catch (e) {
          // Leave whiteboardState in sync with reality (blank/closed)
          // rather than stuck showing "(viewing)" over an empty tile — the
          // next peer-whiteboard-state-change "Start" event will re-attach.
          setWhiteboardState("off");
          throw e;
        }
      }
    } catch (e) {
      console.warn(
        "[ZoomTest] whiteboard reattach after camera change failed",
        e?.type || e?.reason || e?.message || e,
      );
    } finally {
      whiteboardBusyRef.current = false;
    }
  };

  const attachLocalVideo = async (stream, client) => {
    // originalRatio: true — without it, Zoom crops every capture to a
    // 16:9 landscape window. On a phone the camera captures portrait
    // (9:16) video, so that crop discards most of the frame and the
    // tile ends up looking like the camera is off.
    await stream.startVideo({ originalRatio: true });
    const currentUserId = client.getCurrentUserInfo()?.userId;
    if (localVideoContainerRef.current && currentUserId != null) {
      const el = await stream.attachVideo(currentUserId, VideoQuality.Video_360P);
      // See the comment in the peer-video-state-change handler above —
      // object-fit must be set directly on the element to reliably show
      // the whole frame instead of a cropped/zoomed-in view.
      el.style.width = "100%";
      el.style.height = "100%";
      el.style.objectFit = "contain";
      localVideoContainerRef.current.replaceChildren(el);
    }
  };

  // On iOS Safari, the very first attachLocalVideo() after starting the
  // camera commonly renders a black tile — see isIOSSafariBrowser's comment.
  // The only workaround known to actually fix it is what manually toggling
  // the camera off and back on already does: a real stopVideo()/startVideo()
  // cycle, not just re-attaching the same element. Run that cycle once,
  // automatically, shortly after the first attach, so the user doesn't have
  // to notice a black self-view and retoggle it themselves.
  const scheduleIOSSafariSelfViewRecovery = (stream, client) => {
    if (!isIOSSafariBrowser()) return;
    if (iosSelfViewRecoveryTimeoutRef.current) clearTimeout(iosSelfViewRecoveryTimeoutRef.current);
    iosSelfViewRecoveryTimeoutRef.current = setTimeout(async () => {
      iosSelfViewRecoveryTimeoutRef.current = null;
      // The user may have toggled the camera off again, or left the
      // meeting, in the time since this was scheduled.
      if (!cameraOnRef.current || streamRef.current !== stream || clientRef.current !== client) return;
      try {
        const currentUserId = client.getCurrentUserInfo()?.userId;
        if (currentUserId != null) {
          const els = await stream.detachVideo(currentUserId);
          (Array.isArray(els) ? els : [els]).forEach((e) => e?.remove?.());
        }
        await stream.stopVideo();
        if (!cameraOnRef.current) return; // toggled off while stopVideo() was in flight
        await attachLocalVideo(stream, client);
      } catch (e) {
        console.warn("[ZoomTest] iOS Safari self-view recovery cycle failed", e);
      }
    }, 1200);
  };

  const toggleCamera = async () => {
    const stream = streamRef.current;
    const client = clientRef.current;
    if (!stream || !client) return;
    try {
      if (cameraOn) {
        if (iosSelfViewRecoveryTimeoutRef.current) {
          clearTimeout(iosSelfViewRecoveryTimeoutRef.current);
          iosSelfViewRecoveryTimeoutRef.current = null;
        }
        const currentUserId = client.getCurrentUserInfo()?.userId;
        if (currentUserId != null) {
          const els = await stream.detachVideo(currentUserId);
          (Array.isArray(els) ? els : [els]).forEach((e) => e?.remove?.());
        }
        await stream.stopVideo();
        setCameraOn(false);
        cameraOnRef.current = false;
        await reattachWhiteboardIfNeeded();
      } else {
        await attachLocalVideo(stream, client);
        setCameraOn(true);
        cameraOnRef.current = true;
        await reattachWhiteboardIfNeeded();
        scheduleIOSSafariSelfViewRecovery(stream, client);
      }
    } catch (e) {
      console.warn("[ZoomTest] toggleCamera failed", e);
      setError(e?.message || String(e));
    }
  };

  const toggleScreenShare = async () => {
    const stream = streamRef.current;
    if (!stream || !screenCanvasRef.current) return;
    try {
      if (sharingScreen) {
        await stream.stopShareScreen();
        setSharingScreen(false);
      } else {
        await stream.startShareScreen(screenCanvasRef.current);
        setSharingScreen(true);
      }
    } catch (e) {
      console.warn("[ZoomTest] toggleScreenShare failed", e);
      setError(e?.message || String(e));
    }
  };

  // Zoom's cloud recorder joins the session as a bot to actually start/stop
  // recording, which commonly takes several seconds — the start/stop calls
  // below only confirm the *request* was accepted, not that recording has
  // actually changed state. The old code cleared recordingBusy (and
  // re-synced recordingState) the instant that promise resolved, so the
  // button would flip back to "idle" and become clickable again well before
  // anything had actually happened — that's the "very laggy" button: a
  // click looked like it did nothing, so it invited another click, which
  // could race the still-pending first request (Zoom's SDK rejects that
  // with an INVALID_OPERATION "recording state does not match" error).
  // Now recordingBusy instead stays true — with the button visibly
  // pending — until the "recording-change" event actually confirms the
  // new state; the timeout below is only a fallback in case that event is
  // ever dropped. So the wait doesn't read as an unresponsive click, the
  // button also switches to its target look immediately via
  // pendingRecordingState (green + "Starting..."/"Pausing..." etc.), while
  // staying disabled until confirmation.
  const armRecordingBusyFallback = (rec) => {
    if (recordingBusyTimeoutRef.current) clearTimeout(recordingBusyTimeoutRef.current);
    recordingBusyTimeoutRef.current = setTimeout(() => {
      recordingBusyTimeoutRef.current = null;
      setRecordingState(rec.getCloudRecordingStatus());
      setPendingRecordingState(null);
      setRecordingBusy(false);
    }, 10000);
  };

  const toggleRecording = async () => {
    const rec = recordingClientRef.current;
    if (!isHost || !rec || recordingBusy) return;
    setRecordingBusy(true);
    try {
      if (recordingState === "Recording" || recordingState === "Paused") {
        setPendingRecordingState("Stopped");
        const res = await rec.stopCloudRecording();
        if (res) {
          setError(String(res));
          setPendingRecordingState(null);
          setRecordingBusy(false);
          return;
        }
      } else {
        if (!rec.canStartRecording()) {
          setError(
            "Cloud recording isn't enabled for this account/session (check Cloud Recording is turned on for this Video SDK app in the Zoom Marketplace).",
          );
          setRecordingBusy(false);
          return;
        }
        setPendingRecordingState("Recording");
        const res = await rec.startCloudRecording();
        if (res) {
          setError(String(res));
          setPendingRecordingState(null);
          setRecordingBusy(false);
          return;
        }
      }
      armRecordingBusyFallback(rec);
    } catch (e) {
      console.warn("[ZoomTest] toggleRecording failed", e);
      setError(e?.message || String(e));
      setPendingRecordingState(null);
      setRecordingBusy(false);
    }
  };

  const togglePauseRecording = async () => {
    const rec = recordingClientRef.current;
    if (!isHost || !rec || recordingBusy) return;
    setRecordingBusy(true);
    try {
      if (recordingState === "Paused") {
        setPendingRecordingState("Recording");
        const res = await rec.resumeCloudRecording();
        if (res) {
          setError(String(res));
          setPendingRecordingState(null);
          setRecordingBusy(false);
          return;
        }
      } else if (recordingState === "Recording") {
        setPendingRecordingState("Paused");
        const res = await rec.pauseCloudRecording();
        if (res) {
          setError(String(res));
          setPendingRecordingState(null);
          setRecordingBusy(false);
          return;
        }
      } else {
        setRecordingBusy(false);
        return;
      }
      armRecordingBusyFallback(rec);
    } catch (e) {
      console.warn("[ZoomTest] togglePauseRecording failed", e);
      setError(e?.message || String(e));
      setPendingRecordingState(null);
      setRecordingBusy(false);
    }
  };

  // What the recording buttons should show right now: the state we've asked
  // Zoom for (if a request is in flight) so the UI reacts on click, otherwise
  // the confirmed state.
  const shownRecordingState = pendingRecordingState ?? recordingState;
  const shownRecordingActive =
    shownRecordingState === "Recording" || shownRecordingState === "Paused";
  const recordingBusyLabel =
    pendingRecordingState === "Recording"
      ? recordingState === "Paused"
        ? "Resuming..."
        : "Starting..."
      : pendingRecordingState === "Paused"
        ? "Pausing..."
        : pendingRecordingState === "Stopped"
          ? "Stopping..."
          : "Working...";

  const startPresenting = async () => {
    if (whiteboardBusyRef.current) return;
    const wb = whiteboardClientRef.current;
    if (!wb || !whiteboardContainerRef.current) return;
    if (!wb.canStartWhiteboard()) {
      setError("This session doesn't allow you to start a whiteboard right now.");
      return;
    }
    whiteboardBusyRef.current = true;
    try {
      await wb.startWhiteboardScreen(whiteboardContainerRef.current);
      setWhiteboardState("presenting");
      nudgeWhiteboardResize();
    } catch (e) {
      console.warn("[ZoomTest] auto-start whiteboard failed", e);
      setError(e?.message || String(e));
    } finally {
      whiteboardBusyRef.current = false;
    }
  };

  const toggleWhiteboard = async () => {
    if (whiteboardBusyRef.current) return;
    const wb = whiteboardClientRef.current;
    if (!isHost || !wb || !whiteboardContainerRef.current) return;
    if (whiteboardState === "off") {
      await startPresenting();
      return;
    }
    whiteboardBusyRef.current = true;
    try {
      if (whiteboardState === "presenting") {
        await wb.stopWhiteboardScreen();
        setWhiteboardState("off");
      }
      // if "viewing" (someone else is presenting), do nothing — wait for them to stop.
    } catch (e) {
      console.warn("[ZoomTest] toggleWhiteboard failed", e);
      setError(e?.message || String(e));
    } finally {
      whiteboardBusyRef.current = false;
    }
  };

  const rs = roomStyles(isMobile);

  const controlContent = (
    <>
      <div style={rs.iconWrapper}>
        <button
          style={{ ...rs.btn, backgroundColor: micOn ? "#16a34a" : "#dc2626" }}
          onClick={toggleMic}
        >
          <FaMicrophone />
        </button>
        <span style={rs.btnLabel}>Mic</span>
      </div>

      <div style={rs.iconWrapper}>
        <button
          style={{ ...rs.btn, backgroundColor: cameraOn ? "#16a34a" : "#dc2626" }}
          onClick={toggleCamera}
        >
          {cameraOn ? <BsCameraVideoFill /> : <BsCameraVideo />}
        </button>
        <span style={rs.btnLabel}>Camera</span>
      </div>

      {/* <div style={rs.iconWrapper}>
        <button
          style={{ ...rs.btn, backgroundColor: sharingScreen ? "#16a34a" : "#374151" }}
          onClick={toggleScreenShare}
        >
          <FaDesktop />
        </button>
        <span style={rs.btnLabel}>Share</span>
      </div>  */}

      {/* {isHost && (
        <div style={rs.iconWrapper}>
          <button
            style={{
              ...rs.btn,
              backgroundColor: whiteboardState === "presenting" ? "#16a34a" : "#374151",
            }}
            onClick={toggleWhiteboard}
          >
            <FaChalkboard />
          </button>
          <span style={rs.btnLabel}>Board</span>
        </div>
      )} */}

      {isHost && (
        <div style={rs.iconWrapper}>
          <button
            style={{
              ...rs.btn,
              backgroundColor: shownRecordingActive ? "#16a34a" : "#374151",
              opacity: recordingBusy ? 0.85 : 1,
              animation: recordingBusy ? "zoom-recording-pulse 1s ease-in-out infinite" : "none",
              cursor: recordingBusy ? "wait" : "pointer",
            }}
            onClick={toggleRecording}
            disabled={recordingBusy}
            title={
              recordingBusy
                ? "Working — Zoom's cloud recorder can take a few seconds to start or stop"
                : recordingState === "Stopped"
                  ? "Start cloud recording"
                  : "Stop cloud recording"
            }
          >
            {shownRecordingActive ? <BsRecordCircleFill /> : <BsRecordCircle />}
          </button>
          <span style={rs.btnLabel}>{recordingBusy ? recordingBusyLabel : "Record"}</span>
        </div>
      )}

      {isHost && shownRecordingActive && (
        <div style={rs.iconWrapper}>
          <button
            style={{
              ...rs.btn,
              backgroundColor: "#374151",
              opacity: recordingBusy ? 0.6 : 1,
              cursor: recordingBusy ? "wait" : "pointer",
            }}
            onClick={togglePauseRecording}
            disabled={recordingBusy}
            title={shownRecordingState === "Paused" ? "Resume recording" : "Pause recording"}
          >
            {shownRecordingState === "Paused" ? <BsPlayFill /> : <BsPauseFill />}
          </button>
          <span style={rs.btnLabel}>{shownRecordingState === "Paused" ? "Resume" : "Pause"}</span>
        </div>
      )}

      <div style={rs.iconWrapper}>
        <button style={{ ...rs.btn, backgroundColor: "#16a34a" }} onClick={handleLeave}>
          <MdCallEnd />
        </button>
        <span style={rs.btnLabel}>End</span>
      </div>
    </>
  );

  const pageStyle = joined
    ? {
        ...styles.page,
        maxWidth: "100vw",
        width: "100vw",
        minHeight: "100vh",
        margin: 0,
        padding: 0,
      }
    : styles.page;

  // A storage/identity-resumed meeting (unlike a fresh booking-flow
  // navigation) has nowhere else to send the user back to if rejoining
  // fails — e.g. the meeting genuinely ended while this tab was refreshing.
  // Fall back to the manual sandbox instead of leaving them stuck on
  // "Reconnecting…" forever.
  const resumeFailed =
    !!error && !connecting && !joined && !!resumeTarget?.sessionName && !bookingState?.sessionName;
  // Covers both "we know what to resume" and "still checking whether we do"
  // (the master-identity lookup) — keeps the sandbox from flashing on screen
  // for the split second before that check resolves.
  const showingResumeScreen = (resumeTarget?.sessionName || resolvingIdentity) && !resumeFailed;

  return (
    <div style={pageStyle}>
      {!joined && showingResumeScreen && (
        <>
          <h2 style={styles.heading}>
            {bookingState?.sessionName
              ? "Joining your appointment…"
              : resolvingIdentity
                ? "Checking for an active meeting…"
                : "Reconnecting to your meeting…"}
          </h2>
          <p style={styles.subheading}>
            {error ? "Couldn't connect to the meeting." : "Setting up the video call, one moment."}
          </p>
        </>
      )}

      {!joined && !showingResumeScreen && (
        <>
          <h2 style={styles.heading}>Zoom Video SDK — Test Page</h2>
          <p style={styles.subheading}>
            Standalone sandbox to try Zoom calling. Open this page in two
            browser tabs/devices with the same session name to test a call
            between two participants.
          </p>
        </>
      )}

      {!joined && !showingResumeScreen && (
        <div style={styles.configBox}>
          <div style={styles.configRow}>
            <label style={styles.label}>SDK Key</label>
            <input
              style={styles.input}
              type="text"
              value={sdkKey}
              onChange={(e) => setSdkKey(e.target.value)}
              placeholder="Zoom Video SDK Key"
            />
          </div>
          <div style={styles.configRow}>
            <label style={styles.label}>SDK Secret</label>
            <input
              style={styles.input}
              type="password"
              value={sdkSecret}
              onChange={(e) => setSdkSecret(e.target.value)}
              placeholder="Zoom Video SDK Secret"
            />
          </div>
          <div style={styles.configRow}>
            <label style={styles.label}>Session Name</label>
            <input
              style={styles.input}
              type="text"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              placeholder={joinedViaLink ? "" : "Leave blank to auto-generate when creating"}
              readOnly={joinedViaLink}
            />
          </div>
          <div style={styles.configRow}>
            <label style={styles.label}>Display Name</label>
            <input
              style={styles.input}
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />
          </div>

          {joinedViaLink ? (
            <p style={styles.hint}>
              This link was shared by the meeting master — you'll join as a participant.
            </p>
          ) : (
            <p style={styles.hint}>
              Create a meeting to become the master (host), or enter a session
              name to join one someone else created.
            </p>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            {!joinedViaLink && (
              <button style={styles.joinBtn} onClick={handleCreateMeeting} disabled={connecting}>
                {connecting ? "Starting..." : "Create Meeting"}
              </button>
            )}
            <button
              style={{ ...styles.joinBtn, backgroundColor: "#374151" }}
              onClick={handleJoinMeeting}
              disabled={connecting}
            >
              {connecting ? "Joining..." : "Join Meeting"}
            </button>
          </div>
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {joined && (
        <div style={rs.room}>
          <style>{`
            .zoom-appointment-user video-player-container,
            .zoom-appointment-user video-player,
            .zoom-appointment-user video,
            .zoom-appointment-user canvas,
            .zoom-appointment-spotlight video-player-container,
            .zoom-appointment-spotlight video-player,
            .zoom-appointment-spotlight video,
            .zoom-appointment-spotlight canvas {
              width: 100% !important;
              height: 100% !important;
              object-fit: contain !important;
            }
            @media (max-width: 768px) {
              .zoom-appointment-user { width: 84vw !important; min-width: 84vw !important; max-width: 84vw !important; height: auto !important; aspect-ratio: 16 / 9 !important; max-height: 24vh !important; }
              .zoom-appointment-user video-player-container, .zoom-appointment-user video-player, .zoom-appointment-user video, .zoom-appointment-user canvas { max-height: 24vh !important; }
            }
            @keyframes zoom-recording-pulse {
              0%, 100% { opacity: 0.85; }
              50% { opacity: 0.45; }
            }
          `}</style>

          <div style={rs.leftPanel} ref={leftPanelRef}>
            <div style={rs.userList}>
              <div style={rs.user} className="zoom-appointment-user">
                <video-player-container ref={localVideoContainerRef} style={rs.videoContainer} />
                {!cameraOn && <div style={styles.cameraOffOverlay}>Camera off</div>}
                <samp style={rs.userName}>{userName} (you)</samp>
              </div>

              {remoteUsers.map((u) => {
                const isExpanded = expandedRemoteUserId === u.userId;
                return (
                  <div style={rs.user} className="zoom-appointment-user" key={u.userId}>
                    <video-player-container
                      ref={setRemoteVideoContainerRef(u.userId)}
                      style={rs.videoContainer}
                    />
                    {isExpanded && (
                      <div style={styles.cameraOffOverlay}>Enlarged →</div>
                    )}
                    <button
                      type="button"
                      style={rs.enlargeBtn}
                      onClick={() =>
                        setExpandedRemoteUserId((cur) =>
                          cur === u.userId ? null : u.userId,
                        )
                      }
                      title={
                        isExpanded
                          ? "Shrink back to normal size"
                          : "Enlarge this camera to whiteboard size"
                      }
                    >
                      {isExpanded ? "–" : "⛶"}
                    </button>
                    <samp style={rs.userName}>{u.displayName || `User ${u.userId}`}</samp>
                  </div>
                );
              })}
            </div>

            {isHost && remoteHeading != null && (
              <div style={styles.compassDock}>
                <span style={styles.compassDockLabel}>Client compass</span>
                <RemoteCompass heading={remoteHeading} stale={remoteHeadingStale} size={compassSize} />
              </div>
            )}

            {!isMobile && <div style={rs.control}>{controlContent}</div>}
          </div>

          <div style={rs.rightPanel}>
            {/* Spotlight tile — a remote participant's camera blown up to the
                same footprint as the whiteboard. Sits as an absolute overlay
                over the right panel so the whiteboard widget underneath stays
                mounted (and its Zoom session untouched) while it's covered.
                syncRemoteVideos moves that user's video stream into here (at a
                higher quality) whenever expandedRemoteVideoContainerRef is
                mounted, and back to the small left-panel tile when it isn't. */}
            {expandedRemoteUserId != null && (
              <div ref={spotlightTileRef} style={rs.spotlightTile} className="zoom-appointment-spotlight">
                <video-player-container
                  ref={expandedRemoteVideoContainerRef}
                  style={rs.videoContainer}
                />
                <button
                  type="button"
                  style={{ ...rs.fullscreenBtn, left: 8, right: "auto" }}
                  onClick={() => toggleTileFullscreen(spotlightTileRef.current)}
                  title={fullscreenTile === "spotlight" ? "Exit full screen" : "Enlarge to full screen"}
                >
                  {fullscreenTile === "spotlight" ? "⤡" : "⛶"}
                </button>
                <button
                  type="button"
                  style={rs.shrinkBtn}
                  onClick={() => setExpandedRemoteUserId(null)}
                  title="Shrink back to normal size"
                >
                  Shrink ✕
                </button>
                <div style={rs.tileLabel}>
                  {(() => {
                    const u = remoteUsers.find((x) => x.userId === expandedRemoteUserId);
                    return (u && (u.displayName || `User ${u.userId}`)) || "Participant";
                  })()}
                </div>
              </div>
            )}

            {/* {isHost && meetingLink && (
              <div style={styles.linkBox}>
                <label style={styles.label}>Meeting link (share with customer)</label>
                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  <input style={styles.input} type="text" value={meetingLink} readOnly />
                  <button style={styles.copyBtn} onClick={copyMeetingLink}>
                    {linkCopied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            )} */}

            {/* Carries either a real outgoing screen share (the <canvas>, via
                startShareScreen — only relevant when you're the one sharing)
                or a peer's share — a real screen share OR a whiteboard (the
                <video-player-container>, via attachShareView — see
                applyShareView). Whiteboard presenting DOES eventually flip
                a presenter's `sharerOn` roster flag, just not instantly —
                confirmed by testing that attaching to the presenter right
                when they start fails ("Target user is not sharing"), but
                the roster-driven attach (this component waits for `sharerOn`
                itself, no manual retry) succeeds a moment later.

                On mobile this tile is NOT used for peer content at all — a
                peer "share" in this UI is only ever the host's whiteboard
                (the screen-share buttons are disabled for everyone), and on a
                real mobile browser Zoom flips the presenter's `sharerOn` flag
                during a whiteboard too, so attaching here produced a second,
                empty black "Screen share" box stacked above the real widget.
                Mobile shows the whiteboard solely through the widget tile
                below; this tile stays for a genuine outgoing screen share and
                for the desktop peer-share path. */}
            <div
              style={{
                ...rs.screenShareWrap,
                display:
                  sharingScreen || (remoteSharing && !isMobile) ? "block" : "none",
              }}
            >
              <canvas
                ref={screenCanvasRef}
                width={640}
                height={360}
                style={{ ...rs.canvas, display: sharingScreen ? "block" : "none" }}
              />
              <video-player-container
                ref={shareViewContainerRef}
                style={{ ...rs.videoContainer, display: remoteSharing && !sharingScreen ? "block" : "none" }}
              />
              <div style={rs.tileLabel}>
                {sharingScreen
                  ? "Screen share (you)"
                  : remoteWhiteboardActive
                    ? "Whiteboard"
                    : "Screen share"}
              </div>
            </div>

            {/* The real, interactive whiteboard widget — same startWhiteboardView
                call on desktop and mobile. On mobile the tile gets a concrete
                pixel height (mobileBoardHeight), overflow:hidden, and the
                widget itself is mounted into a WB_FORCE_WIDTH-wide box that's
                then CSS-scaled down (mobileWbScale) so Zoom keeps rendering it
                on its working "desktop" layout instead of the narrow one where
                the drawing canvas never paints. The container div always stays
                mounted regardless — the whiteboard effects all null-check it. */}
            <div
              ref={whiteboardTileRef}
              style={{
                ...rs.whiteboardTile,
                ...(isMobile
                  ? {
                      height: mobileBoardHeight,
                      minHeight: mobileBoardHeight,
                      flex: "0 0 auto",
                      overflow: "hidden",
                    }
                  : {}),
              }}
            >
              <button
                type="button"
                style={rs.fullscreenBtn}
                onClick={() => toggleTileFullscreen(whiteboardTileRef.current)}
                title={fullscreenTile === "whiteboard" ? "Exit full screen" : "Enlarge to full screen"}
              >
                {fullscreenTile === "whiteboard" ? "⤡" : "⛶"}
              </button>
              <div
                ref={whiteboardContainerRef}
                style={
                  isMobile
                    ? {
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: WB_FORCE_WIDTH,
                        height: Math.round(mobileBoardHeight / mobileWbScale),
                        transform: `scale(${mobileWbScale})`,
                        transformOrigin: "top left",
                        backgroundColor: "#fff",
                      }
                    : rs.whiteboardContainer
                }
              />
              {/* Zoom's whiteboard sets pointer-events:auto on its own toolbar/canvas
                  elements, which overrides any pointer-events:none we put on this
                  container — so a CSS block on the container alone doesn't stop
                  drawing. This transparent pane sits above it in the same stacking
                  context and physically catches every click/drag/touch before the
                  widget underneath ever receives them. */}
              {!isHost && <div style={rs.viewOnlyOverlay} />}
              {!isHost && (remoteWhiteboardActive || remoteSharing) && whiteboardState !== "viewing" && (
                <div style={styles.cameraOffOverlay}>Loading whiteboard…</div>
              )}
              <div style={rs.tileLabel}>
                Whiteboard{whiteboardState === "viewing" ? " (viewing)" : ""}
              </div>
            </div>
          </div>

          {isMobile && <div style={rs.controlMobile}>{controlContent}</div>}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: "24px",
    fontFamily: "sans-serif",
    maxWidth: 900,
    margin: "0 auto",
  },
  heading: { marginBottom: 4 },
  subheading: { color: "#555", marginBottom: 20 },
  configBox: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
    backgroundColor: "#f9fafb",
    maxWidth: 480,
  },
  configRow: { display: "flex", flexDirection: "column", gap: 4 },
  hint: { margin: 0, fontSize: 12, color: "#6b7280" },
  linkBox: {
    marginBottom: 16,
    padding: 16,
    border: "1px solid #ddd",
    borderRadius: 8,
    backgroundColor: "#f9fafb",
    maxWidth: 640,
  },
  copyBtn: {
    padding: "8px 16px",
    borderRadius: 6,
    border: "none",
    backgroundColor: "#008CBA",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  input: {
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    fontSize: 14,
  },
  joinBtn: {
    marginTop: 8,
    padding: "10px 16px",
    borderRadius: 6,
    border: "none",
    backgroundColor: "#16a34a",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  error: {
    marginTop: 12,
    padding: "10px 14px",
    borderRadius: 6,
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    maxWidth: 480,
  },
  cameraOffOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    fontSize: 13,
  },
  // In-flow (not fixed) — sits in the left panel, under the video tiles and
  // above the call controls, so it scales with that column instead of
  // floating over content and risking an overlap with the control bar.
  compassDock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    margin: "12px 0",
  },
  compassDockLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#fff",
    backgroundColor: "rgba(10,3,30,0.75)",
    padding: "2px 8px",
    borderRadius: 999,
  },
};

const roomStyles = (isMobile) => ({
  room: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    minHeight: "100vh",
    backgroundColor: "#f2f4f8",
    overflowX: "hidden",
    paddingBottom: isMobile
      ? "calc(env(safe-area-inset-bottom, 0px) + 96px)"
      : 0,
    boxSizing: "border-box",
  },
  leftPanel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: isMobile ? "100%" : "25%",
    padding: isMobile ? "12px" : "20px",
    borderRight: isMobile ? "none" : "1px solid #ddd",
    maxHeight: isMobile ? "30vh" : undefined,
    overflowY: isMobile ? "hidden" : "visible",
    flex: isMobile ? "0 0 auto" : undefined,
    order: isMobile ? 1 : undefined,
    boxSizing: "border-box",
  },
  rightPanel: {
    width: isMobile ? "100%" : "75%",
    padding: isMobile ? "12px" : "20px",
    display: "flex",
    flexDirection: "column",
    // flex-start on mobile (not center): the whiteboard tile now carries its
    // own fixed pixel height and must start right below the user strip, not
    // float in the middle of whatever space is left.
    justifyContent: "flex-start",
    flex: isMobile ? "1 1 auto" : undefined,
    order: isMobile ? 2 : undefined,
    overflowY: isMobile ? "auto" : undefined,
    boxSizing: "border-box",
    // Positioning context for the whiteboard tile's absolutely-filled child
    // (see whiteboardContainer).
    position: "relative",
  },
  userList: {
    display: "flex",
    gap: "1.25rem",
    flexWrap: isMobile ? "nowrap" : "wrap",
    justifyContent: isMobile ? "flex-start" : "center",
    flexDirection: "row",
    width: "100%",
    overflowX: isMobile ? "auto" : "visible",
    overflowY: "hidden",
    paddingBottom: isMobile ? "6px" : 0,
    paddingInline: isMobile ? "4px" : 0,
    WebkitOverflowScrolling: "touch",
  },
  user: {
    border: "1px solid rgba(75, 85, 99, 1)",
    // Same principle on desktop as mobile: size the tile off aspect-ratio
    // (matching a typical webcam's 16:9 capture) instead of a fixed
    // width/height pair — a fixed box shape forces object-fit: contain to
    // letterbox/pillarbox far more than necessary, making the picture look
    // small inside big black bars.
    width: isMobile ? "84vw" : "20vw",
    minWidth: isMobile ? "84vw" : undefined,
    maxWidth: isMobile ? "84vw" : undefined,
    height: "auto",
    aspectRatio: "16 / 9",
    maxHeight: isMobile ? "24vh" : "30vh",
    borderRadius: "8px",
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    flexShrink: 0,
    backgroundColor: "#111",
  },
  userName: {
    color: "#fff",
    fontSize: "1rem",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    position: "absolute",
    bottom: "0",
    width: "100%",
    padding: "0.5rem 0",
    textAlign: "center",
    fontWeight: "bold",
  },
  control: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: isMobile ? "12px" : "10px 8px",
    backgroundColor: "#2b2b2b",
    width: isMobile ? "100%" : "90%",
    marginTop: isMobile ? "0" : "auto",
    flexWrap: "wrap",
    gap: "10px",
    boxSizing: "border-box",
    alignContent: "flex-start",
    maxHeight: isMobile ? "none" : "35vh",
    overflowY: "auto",
    position: isMobile ? "sticky" : "static",
    bottom: isMobile ? 0 : "auto",
    left: isMobile ? 0 : "auto",
    zIndex: isMobile ? 150 : "auto",
  },
  controlMobile: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px 6px",
    paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
    backgroundColor: "rgba(43,43,43,0.98)",
    gap: "6px",
    zIndex: 999,
    boxSizing: "border-box",
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  btn: {
    display: "inline-flex",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "6px",
    padding: isMobile ? "8px 10px" : "6px 10px",
    color: "#fff",
    margin: isMobile ? "0 2px" : "0 6px",
    cursor: "pointer",
    minWidth: isMobile ? "34px" : "40px",
    minHeight: isMobile ? "34px" : undefined,
    justifyContent: "center",
    alignItems: "center",
  },
  iconWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: isMobile ? 0 : "0 2px",
    flex: "0 1 auto",
  },
  btnLabel: {
    marginTop: "4px",
    fontSize: isMobile ? "10px" : "12px",
    color: "white",
  },
  videoContainer: {
    display: "block",
    width: "100%",
    height: "100%",
    backgroundColor: "#000",
  },
  screenShareWrap: {
    width: "100%",
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid rgba(75,85,99,1)",
    position: "relative",
    boxSizing: "border-box",
    backgroundColor: "#000",
    // On mobile this tile is what actually carries the whiteboard for a
    // non-host viewer (the widget tile is hidden once it attaches — see its
    // comment), so it takes the right panel's full height rather than a
    // small corner overlay. Desktop keeps the normal stacked layout, since
    // the widget there is the primary (and usually only) whiteboard view.
    ...(isMobile
      ? { flex: "1 1 auto", minHeight: "52vh", marginBottom: 0 }
      : { marginBottom: 16 }),
  },
  canvas: { display: "block", width: "100%", backgroundColor: "#000" },
  // No `overflow: hidden` and no border/padding directly on this element —
  // Zoom's whiteboard renders its own toolbar as an absolutely-positioned
  // overlay anchored to this container, so it needs `position: relative`
  // and unclipped space around it or the drawing tools get cut off/inert.
  whiteboardTile: {
    position: "relative",
    borderRadius: 8,
    border: "1px solid rgba(75,85,99,1)",
    flex: "1 1 auto",
    minHeight: isMobile ? "52vh" : "75vh",
  },
  // Absolutely fill whiteboardTile rather than carrying its own fixed
  // height — whiteboardTile's actual rendered height comes from flex-grow,
  // which doesn't always match a hardcoded vh value on this child, leaving
  // a dead gap below the board (and stranding tileLabel below it).
  whiteboardContainer: {
    position: "absolute",
    inset: 0,
    display: "block",
    width: "100%",
    height: "100%",
    backgroundColor: "#fff",
  },
  tileLabel: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    padding: "4px 0",
    textAlign: "center",
    color: "#fff",
    backgroundColor: "rgba(0,0,0,0.6)",
    fontSize: 12,
    zIndex: 2,
  },
  // Full-screen toggle (host and participant both get it), top-right corner
  // of the whiteboard tile (top-left in the spotlight tile, to clear its
  // shrinkBtn) — see toggleTileFullscreen.
  fullscreenBtn: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 3,
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.15)",
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    fontSize: 15,
    lineHeight: 1,
    cursor: "pointer",
  },
  // Small toggle in the corner of a remote participant's tile that blows
  // their camera up to whiteboard size (and back).
  enlargeBtn: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 3,
    width: 26,
    height: 26,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.15)",
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    fontSize: 13,
    lineHeight: 1,
    cursor: "pointer",
  },
  // The enlarged remote camera. Absolute overlay filling the right panel
  // (minus its padding) so it lands exactly on the whiteboard's footprint
  // without unmounting the board underneath.
  spotlightTile: {
    position: "absolute",
    top: isMobile ? 12 : 20,
    left: isMobile ? 12 : 20,
    right: isMobile ? 12 : 20,
    bottom: isMobile ? 12 : 20,
    minHeight: isMobile ? "52vh" : "75vh",
    borderRadius: 8,
    border: "1px solid rgba(75,85,99,1)",
    overflow: "hidden",
    backgroundColor: "#000",
    zIndex: 20,
    boxSizing: "border-box",
  },
  shrinkBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 3,
    padding: "6px 12px",
    borderRadius: 6,
    border: "none",
    backgroundColor: "rgba(0,0,0,0.65)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  // Transparent pane stacked above the whiteboard widget for viewers — see
  // the comment at its usage site for why pointer-events:none on the
  // widget's own container isn't enough.
  viewOnlyOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    background: "transparent",
    cursor: "default",
    touchAction: "none",
  },
});
